 import React from 'react';
 import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';

 interface ChartProps {
    title: string;
    id: number;
    height: number;
 }

 interface VelocityMixData {
    product_grps_1: string[];
    product_grps_2: string[];
    product_grp_1: string | null;
    product_grp_2: string | null;
    sales: {
        product_1?: number[];
        product_2?: number[];
    };
 }

 export default function VelocityMix(props: ChartProps) {

    const [data, setData] = React.useState<VelocityMixData | null>(null);
    const [loading, setLoading] = React.useState<boolean>(true);
    const chartComponentRef = React.useRef<HighchartsReact.RefObject>(null);
    React.useEffect(() => {
        const fetchData = async (
            product_grp_1?: string | null,
            product_grp_2?: string | null
        ) => {
            try {
                setLoading(true);
                const params = new URLSearchParams({
                    entity_id: localStorage.getItem('entity_id') ?? ''
                });
                if (product_grp_1 && product_grp_2) {
                    params.set('product_grp_1', product_grp_1);
                    params.set('product_grp_2', product_grp_2);
                }
                const response = await fetch(
                    `/dashboard/velocity-mix/${props.id}/?${params.toString()}`
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch data');
                }
                const data = await response.json();
                setData(data);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData(data?.product_grp_1, data?.product_grp_2);
    }, [data?.product_grp_1, data?.product_grp_2, props.id]);

    const options: Highcharts.Options = {
        chart: {
            type: 'column',
            zooming: {
                type: 'xy' as const
            }
        },
        title: {
            text: props.title,
            align: 'left',
            style: {
                fontFamily: 'Trebuchet MS'
            }
        },
        xAxis: {
            categories: ['A', 'B', 'C', 'D'],
            crosshair: true,
            accessibility: {
                description: 'Velocity Codes'
            },
            title: {
                text: 'Products',
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Gross Margin (%)',
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            },
            labels: {
                formatter: function(this: Highcharts.AxisLabelsFormatterContextObject) {
                    return (this.value as number) + '%';
                },
                style: {
                    fontFamily: 'Trebuchet MS'
                }
            }
        },
        tooltip: {
            valueSuffix: ' (%)'
        },
        plotOptions: {
            column: {
                pointPadding: 0.2,
                borderWidth: 0
            }
        },
        series: [
            {
                type: 'column',
                name: loading ? 'Product 1' : data?.product_grp_1 ?? 'Product 1',
                data: loading ? [] : data?.sales?.product_1 ?? []
            },
            {
                type: 'column',
                name: loading ? 'Product 2' : data?.product_grp_2 ?? 'Product 2',
                data: loading ? [] : data?.sales?.product_2 ?? []
            }
        ]   
    }
    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', flexGrow: 1 }}>
            {loading && !data ? (
                <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress />
                </Box>
            ) : data && data.product_grp_1 && data.product_grp_2 ? (
                <>
                    {loading ? (
                        <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', verticalAlign: 'middle', flexGrow: 1 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{ height: '100%', width: '100%', display: 'column', justifyContent: 'center', flexGrow: 1, gap: 3 }}>
                            <Box sx={{ height: '25%', width: '100%', display: 'flex', justifyContent: 'center', flexGrow: 1, gap: 3, paddingTop: 3 }}>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    sx={{ width: '100%' }}
                                    id="product_grp_1"
                                    select
                                    label="Product 1"
                                    value={data && data.product_grp_1 || ''}
                                    onChange={(event) => setData({ ...data, product_grp_1: event.target.value })}
                                >
                                    {data && data.product_grps_1.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    size="small"
                                    variant="standard"
                                    sx={{ width: '100%' }}
                                    id="product_grp_2"
                                    select
                                    label="Product 2"
                                    value={data && data.product_grp_2 || ''}
                                    onChange={(event) => setData({ ...data, product_grp_2: event.target.value })}
                                >
                                    {data && data.product_grps_2.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                            <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', flexGrow: 1 }}>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={options}
                                    allowChartUpdate={true}
                                    exportEnabled={true}
                                    ref={chartComponentRef}
                                    containerProps={{ style: { width: '100%', height: '70%', borderRadius: 5, boxShadow: 'none', margin: '10px 0px 0px 0px', padding: 0 } }}
                                />
                            </Box>
                        </Box>
                    )}
                </>
            ) : (
                <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center' }}>No data available</Typography>
            )}
        </Box>
    )
 }